import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import get from 'lodash-es/get';

import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { ModalChildComponent } from '@finder/shared/components/modal/modal-child-component.interface';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { ReplacePlaceholdersPipe } from '@finder/shared/utils/pipes/replace-placeholders/replace-placeholders.pipe';
import { RaInputComponent } from '../../ra-wrappers/ra-input/ra-input.component';
import { Subject, take } from 'rxjs';

@Component({
    selector: 'finder-currency-converter-modal',
    templateUrl: './currency-converter.modal.component.html',
    styleUrls: ['./currency-converter.modal.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    imports: [
        CommonModule,
        ReplacePlaceholdersPipe,
        RaInputComponent,
    ],
    providers: [DecimalPipe]
})
export class CurrencyConverterModalComponent implements ModalChildComponent, OnDestroy, AfterViewInit {

    @ViewChild('targetInput', { read: ElementRef }) targetInput: ElementRef;

    private baseInput;
    private elmResolveSubject$ = new Subject();

    data: any;
    parentComponent: ModalComponent;
    conversionRate: number;
    baseInputElm: any;
    targetInputElm: any;
    clearButtonHandler: (evt) => void;
    lastInputValue: string;
    targetCurrencyCode: string;
    appLocale: string;

    constructor(
        // Since CurrencyPipe doesn't support our i18n needs then use decimal pipe instead.
        private decimalPipe: DecimalPipe,
        private localeService: LocaleService,
    ) {
        /**
         * @TODO Move this logic to APP Component so that the locale is applied globally
         * @see https://stackoverflow.com/questions/44287827/dynamically-change-locale-for-datepipe-in-angular-2
         * @see https://angular.io/guide/i18n#setting-up-the-locale-of-your-app
         */
        this.appLocale = this.localeService.getLocale();
    }

    ngAfterViewInit() {
        this.elmResolveSubject$
            .pipe(take(1))
            .subscribe(() => {
                this.conversionRate = get(this.data, 'conversionRate', 1);
                this.targetCurrencyCode = get(this.data, 'targetCurrencyCode', 'USD');
                this.baseInputElm = this.baseInput.raComponent.nativeElement;
                this.targetInputElm = this.targetInput.nativeElement;

                this.clearButtonHandler = (evt) => {
                    // After clearing the input, change its value to $1.
                    this.baseInputElm.value = '$1';
                    this.convert(evt);
                };

                this.baseInputElm.addEventListener('icon-button-click', this.clearButtonHandler);
                this.targetInputElm.value = this.formatCurrency(this.conversionRate);

                // select text in the wdpr-input (across the shadow DOM) on click
                const shadowRoot = this.baseInput.raComponent.nativeElement.shadowRoot;
                if (shadowRoot) {
                    const nativeInput = shadowRoot.querySelector('input');
                    if (nativeInput) {
                        nativeInput.addEventListener('click', () => nativeInput.select());
                    }
                }
            });
    }

    ngOnDestroy() {
        // Prevent memory leaks by removing attached event listener.
        this.baseInputElm.removeEventListener('icon-button-click', this.clearButtonHandler);
    }

    setInputComponent(component) {
        /**
         * Wait for this component to load completely before letting the code in ngAfterViewInit
         * run. It's looking for this component and it has to load async.
         */
        this.baseInput = component;
        this.elmResolveSubject$.next(true);
    }

    /**
     * @description Handles price conversion on base currency input text change
     * @param evt Input Event fired after introducing numbers in the base currency input
     */
    convert(evt) {
        // Normalize input
        let price = evt.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        // Remember cursor positioning
        let start = this.baseInputElm.selectionStart;
        let end = this.baseInputElm.selectionEnd;

        // If the guest introduces a single dot (which is valid), then normalize input with a leading 0.
        if (price === '.') {
            price = '0.';
            start += 1;
            end += 1;
        }

        price = this.checkMaxDecimalDigits(price);
        price = this.checkMaxIntegerDigits(price, start, end);

        this.targetInputElm.value = this.formatCurrency(price * this.conversionRate);
        // prepend the currency symbol to the beginning of the price.
        price = `${this.data.currency.baseCurrency.symbol}${price}`;

        // If price didn't change means an invalid character was inserted.
        if (this.lastInputValue === price) {
            // Decrease start and end selection range in one unit to avoid moving the cursor
            // when introducing a non valid character.
            start -= 1;
            end -= 1;
        }

        // Update the input value with formatted price
        this.baseInputElm.value = price;
        this.lastInputValue = price;

        this.baseInputElm.setSelectionRange(start, end);
    }

    handleCursorPosition(evt) {
        const start = this.baseInputElm.selectionStart;
        const end = this.baseInputElm.selectionEnd;

        if (start === 0) {
            // If user tries to erase currency sign ($) set cursor position after it.
            this.baseInputElm.setSelectionRange(start + 1, end + 1);
        }
    }

    /**
     * @description A custom currency formatter
     *              ie: 18.50 -> $18,50 ARS
     *                  23.99 -> $23.99 USD
     *                  15.69 -> R15,69 BRL
     *                  29.99 -> S/.29.99 PEN
     * @param amount The amount to convert
     */
    formatCurrency(amount) {
        const formattedNumber = this.decimalPipe.transform(
            amount,
            '1.2-2',
            this.appLocale
        );

        const symbol = this.data.currency.targetCurrency.symbol;
        const code = this.data.currency.targetCurrency.code;

        return `${symbol}${formattedNumber} ${code}`;
    }

    /**
     * @description limits input up to 2 decimals.
     * @param amount The amount to convert
     */
    checkMaxDecimalDigits(amount: string) {
        const priceChunks = amount.split('.');
        let result = amount;

        if (priceChunks.length > 1) {
            const integer = priceChunks[0];
            const decimal = priceChunks[1];
            const maxDecimalDigits = 2;
            const formattedDecimal = (decimal.length <= maxDecimalDigits) ?
                decimal :
                decimal.slice(0, maxDecimalDigits);

            result = integer + '.' + formattedDecimal;
        }

        return result;
    }

    /**
     * @description limits the input up to 9 decimals.
     * @param amount The amount to convert
     * @param start The start index position of the last entered number
     * @param end The end index position of the last entered number
     */
    checkMaxIntegerDigits(amount: string, start, end) {
        const priceChunks = amount.split('.');
        const integer = priceChunks[0];
        const integerFormated = (integer.length <= 9) ?
            integer :
        // If amount is 123456789.25 and then user inputs 1239456789.25
        // (goes over the max) the returned value is 123456789.25 as
        // the function removes the last number typed
            integer.slice(0, start - 2) + integer.slice(end - 1);

        return (priceChunks.length > 1) ?
            integerFormated + '.' + priceChunks[1] :
            integerFormated;
    }
}
